import * as t from 'io-ts';
import { createActions } from 'redux-actions';

import { ENDPOINTS_TEAMS, ENDPOINTS_USER } from '../../config';
import { localeData } from '../../locales/data';
import { NoConsultantTeamElementIO, TeamElementIO } from '../../reducers/TeamReducer/types';
import { defaultErrorMessage, globalFetch, isAction } from '../../shared/helpers/fetch-helper';
import { logErrorService } from '../../shared/services';
import { actionAgreementState } from '../AgreementsActions';
import { getUserCompetitions } from '../CompetitionsActions';
import { addFlashMessages, removeFlashMessage, removeFlashMessagesContexts } from '../FlashMessagesActions';

export const {
  userTeamsResponse,
  teamUpdateReponseSuccess,
  acceptTeamsResponse,
  rejectTeamsResponse,
  teamsRequest,
  teamsResponseFailure,
  clearFetching,
  clearTeams,
  teamUpdateRequest,
  teamUpdateReponseFailure,
  teamsRequestAll,
  teamsResponseAll,
  teamsAcceptFailure,
  teamsTerminateFailure,
  teamsTerminateSuccess,
  teamsAcceptSuccess,
  teamsRejectFailure,
  teamsRejectSuccess,
  addUniversityToTeams,
} = createActions(
  {
    USER_TEAMS_RESPONSE: (data: any, status: 'ACCEPTED' | 'PENDING' | 'REJECTED', userId: string) => ({ data, status, userId }),
    TEAM_UPDATE_REPONSE_SUCCESS: (data: any, status: 'ACCEPTED' | 'PENDING' | 'REJECTED', userId: string) => ({ data, status, userId }),
  },
  'TEAMS_REQUEST',
  'TEAMS_RESPONSE_FAILURE',
  'CLEAR_FETCHING',
  'CLEAR_TEAMS',
  'TEAM_UPDATE_REQUEST',
  'TEAM_UPDATE_REPONSE_FAILURE',
  'TEAMS_REQUEST_ALL',
  'TEAMS_RESPONSE_ALL',
  'TEAMS_ACCEPT_FAILURE',
  'TEAMS_ACCEPT_SUCCESS',
  'TEAMS_TERMINATE_FAILURE',
  'TEAMS_TERMINATE_SUCCESS',
  'TEAMS_REJECT_FAILURE',
  'TEAMS_REJECT_SUCCESS',
  'ADD_UNIVERSITY_TO_TEAMS'
);

export const fetchTeams =
  (selfStatus?: 'ACCEPTED' | 'PENDING' | 'REJECTED', status?: ('ACTIVE' | 'PENDING' | 'TERMINATED' | 'COMPLETED')[]) =>
  async (dispatch: any, getState: any): Promise<object> => {
    const state = getState();
    dispatch(teamsRequest());

    return globalFetch(
      `${ENDPOINTS_USER.user}/self/teams?${status ? `status=${status.map(statusVar => `${statusVar}`).join('%1f')}&` : ''}${
        selfStatus ? `members.self.status=${selfStatus}&` : ''
      }order=-dateCreated`,
      {
        method: 'GET',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
      },
      dispatch,
      () => dispatch(teamsResponseFailure())
    )
      .then(async (json): Promise<object> => {
        if (isAction(json, 'TEAMS_RESPONSE_FAILURE')) {
          return json;
        } else if (json.hasOwnProperty('detail')) {
          return dispatch(teamsResponseFailure(selfStatus));
        } else {
          const pendingTeam = json.results.find((result: any) => result.status === 'PENDING');
          const acceptedTeam = json.results.find((result: any) => result.status === 'ACTIVE');
          if (pendingTeam && selfStatus === 'ACCEPTED') {
            dispatch(
              addFlashMessages([{ msg: 'team.pending.waiting', defaultMessage: localeData.en['team.pending.waiting'] }], {
                location: 'team',
                type: 'warning',
              })
            );
          } else if (acceptedTeam && selfStatus === 'ACCEPTED') {
            dispatch(removeFlashMessagesContexts(['team'], ['generic']));
          }
          const stautsToSend = status && status.find(constant => constant === 'TERMINATED') ? 'TERMINATED' : selfStatus;

          return dispatch(userTeamsResponse(json, stautsToSend, state.getIn(['user', 'userId'])));
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        defaultErrorMessage(dispatch);

        return dispatch(teamsResponseFailure());
      });
  };

export const fetchAllTeams =
  (responseAction: any = teamsResponseAll) =>
  async (dispatch: any) => {
    dispatch(teamsRequestAll());

    const ACTIVE = dispatch(fetchTeams('ACCEPTED', ['ACTIVE', 'PENDING']));
    const PENDING = dispatch(fetchTeams('PENDING', ['ACTIVE', 'PENDING']));
    const TERMINATED = dispatch(fetchTeams(undefined, ['COMPLETED', 'TERMINATED']));

    return Promise.all([ACTIVE, PENDING, TERMINATED]).then(() => dispatch(responseAction()));
  };

export function fetchActionTeams(teamId: string, actionSuccess: any, actionFailure: any, method: string, actionType: string) {
  return async (dispatch: any): Promise<object> => {
    const callbackStatus400 = (response: any) =>
      response.json().then((json: any) => {
        if (json && Array.isArray(json) && json.length !== 0) {
          const messages = json.map((message: string) => ({ msg: message, defaultMessage: localeData.en[message] }));
          dispatch(addFlashMessages(messages, { intl: false }));
        } else if (json?.detail) {
          const messages = [{ msg: json.detail, defaultMessage: localeData.en[json.detail] }];
          dispatch(addFlashMessages(messages, { intl: false }));
        }
        logErrorService(response, json);

        return dispatch(fetchAllTeams(actionFailure));
      });

    const callbackStatus404 = (response: any) =>
      response.json().then((json: any) => {
        if (json && json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }], { intl: false }));
        }
        logErrorService(response, json);

        return dispatch(fetchAllTeams(actionFailure));
      });

    dispatch(teamsRequestAll());

    return globalFetch(
      `${ENDPOINTS_TEAMS.teams}/${teamId}/${method}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
      },
      dispatch,
      () => dispatch(fetchAllTeams()),
      { callbackStatus400, callbackStatus404, answerEmpty: true }
    )
      .then(async (json): Promise<object> => {
        if (isAction(json, actionType)) {
          return json;
        } else if (json && json.hasOwnProperty('detail')) {
          logErrorService(json.detail, json.detail);
          dispatch(fetchAllTeams());

          return dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
        } else if (json && Array.isArray(json) && json.length !== 0) {
          const messages = json.map((message: string) => ({ msg: message, defaultMessage: localeData.en[message] }));
          dispatch(fetchAllTeams());

          return dispatch(addFlashMessages(messages, { intl: false }));
        } else {
          return dispatch(fetchAllTeams(actionSuccess));
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        defaultErrorMessage(dispatch);

        return dispatch(fetchAllTeams());
      });
  };
}

export const fetchAcceptTeams = (teamId: string) =>
  fetchActionTeams(teamId, teamsAcceptSuccess, teamsAcceptFailure, 'accept', 'TEAMS_ACCEPT_FAILURE');
export const fetchRejectTeams = (teamId: string) =>
  fetchActionTeams(teamId, teamsRejectSuccess, teamsRejectFailure, 'reject', 'TEAMS_REJECT_FAILURE');
export const fetchTerminateTeams = (teamId: string) =>
  fetchActionTeams(teamId, teamsTerminateSuccess, teamsTerminateFailure, 'terminate', 'TEAMS_TERMINATE_FAILURE');

export const fetchAcceptCompetitionAndTeam =
  (competitonId: string, teamId: string) =>
  async (dispatch: any, getState: any): Promise<object> => {
    dispatch(teamsRequestAll());

    return actionAgreementState(competitonId, 'competitions', 'accept', dispatch, getState)
      .then(async (json: any): Promise<object> => {
        if (isAction(json, 'RESPONSE_AGREEMENT_STATE_FAILURE')) {
          return dispatch(teamsResponseAll());
        } else {
          dispatch(getUserCompetitions('self', false));

          return dispatch(fetchAcceptTeams(teamId));
        }
      })
      .catch(error => {
        logErrorService(error, error);

        return dispatch(teamsResponseAll());
      });
  };

export const updateTeam =
  (teamId: string, data: any) =>
  async (dispatch: any, getState: any): Promise<object> => {
    dispatch(teamUpdateRequest());
    dispatch(removeFlashMessage(['top', 'generic']));

    const callbackStatus400 = (response: any) =>
      response
        .json()
        .then((json: any) => {
          if (json && json.errors && json.errors[0] === 'DUPLICATE_TEAM_NAME') {
            dispatch(
              addFlashMessages([
                { msg: 'active.team.duplicate.team.name', defaultMessage: localeData.en['active.team.duplicate.team.name'] },
              ])
            );
            logErrorService('active.team.duplicate.team.name', 'active.team.duplicate.team.name');
          }

          return dispatch(teamUpdateReponseFailure(json));
        })
        .catch();

    return globalFetch(
      `${ENDPOINTS_TEAMS.teams}/${teamId}`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(data),
      },
      dispatch,
      () => dispatch(teamUpdateReponseFailure()),
      {
        callbackStatus400,
      }
    )
      .then(async (json): Promise<object> => {
        if (isAction(json, 'TEAM_UPDATE_REPONSE_FAILURE')) {
          return json;
        } else if (json.detail) {
          dispatch(addFlashMessages([{ msg: json.detail, defaultMessage: localeData.en[json.detail] }]));
          logErrorService(json.detail, json.detail);

          return dispatch(teamUpdateReponseFailure());
        } else {
          const state = getState();
          const userId = state.getIn(['user', 'userId']);

          return dispatch(teamUpdateReponseSuccess({ results: [json] }, 'ACCEPTED', userId));
        }
      })
      .catch(() => {
        dispatch(addFlashMessages([{ msg: 'team.edit.error.message', defaultMessage: localeData.en['team.edit.error.message'] }]));
        logErrorService('Some Went Wrong Editing a team, try again later', 'Some Went Wrong Editing a team, try again later');

        return dispatch(teamUpdateReponseFailure());
      });
  };

export function fetchCreateTeam(data: any) {
  return async (dispatch: any, getState: any): Promise<object> => {
    dispatch(teamUpdateRequest());
    const consultantValidation =
      !t.exact(TeamElementIO).decode(data).isRight() && data.type && (data.type === 'CONSULTANT' || data.type === 'RESEARCHER');
    const noConsultantValidation =
      !t.exact(NoConsultantTeamElementIO).decode(data).isRight() && data.type && data.type !== 'CONSULTANT' && data.type !== 'RESEARCHER';

    if (consultantValidation || noConsultantValidation) {
      dispatch(addFlashMessages([{ msg: 'team.create.error.message', defaultMessage: localeData.en['team.create.error.message'] }]));

      return dispatch(teamUpdateReponseFailure());
    }

    const callbackStatus400 = (response: any) =>
      response
        .json()
        .then((json: any) => {
          if (json && json.errors && json.errors[0] === 'DUPLICATE_TEAM_NAME') {
            dispatch(
              addFlashMessages([
                { msg: 'active.team.duplicate.team.name', defaultMessage: localeData.en['active.team.duplicate.team.name'] },
              ])
            );
            logErrorService('active.team.duplicate.team.name', 'active.team.duplicate.team.name');
          } else if (json && json.errors) {
            dispatch(addFlashMessages(json.errors, { context: 'generic', intl: false }));
          }

          return dispatch(teamUpdateReponseFailure(json));
        })
        .catch();

    return globalFetch(
      ENDPOINTS_TEAMS.teams,
      {
        method: 'POST',
        credentials: 'include',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(data),
      },
      dispatch,
      () => dispatch(teamUpdateReponseFailure()),
      {
        callbackStatus400,
      }
    )
      .then(json => {
        if (isAction(json, 'TEAM_UPDATE_REPONSE_FAILURE')) {
          return json;
        } else if (json.hasOwnProperty('status') && json.hasOwnProperty('id')) {
          const state = getState();
          const userId = state.getIn(['user', 'userId']);

          return dispatch(teamUpdateReponseSuccess({ results: [json] }, 'ACCEPTED', userId));
        } else {
          let errors: string[] = [];

          Object.keys(json).forEach((key: string) => {
            if (key === 'members') {
              json[key].forEach((elem: any) => {
                const value = Object.keys(elem);
                if (value.length) {
                  value.forEach((k: string) => {
                    errors = errors.concat(elem[k]);
                  });
                }
              });
            } else {
              errors = errors.concat(json[key]);
            }
          });

          dispatch(addFlashMessages(errors, { context: 'generic', intl: false }));

          logErrorService(errors, errors);

          return dispatch(teamUpdateReponseFailure());
        }
      })
      .catch((err: any) => {
        logErrorService(err, err);
        defaultErrorMessage(dispatch);

        return dispatch(teamUpdateReponseFailure());
      });
  };
}
